import { useEffect } from 'react'
import type { ReactNode } from 'react'
import ErrorOutline from '@mui/icons-material/ErrorOutline'
import LinkOff from '@mui/icons-material/LinkOff'
import { ReactSVG } from 'react-svg'
import icon from '../assets/logo.svg'
import Box from '@mui/material/Box'
import Skeleton from '@mui/material/Skeleton'
import Typography from '@mui/material/Typography'
import { usePreferences } from '../hooks'

const isExpiredLink = (error?: string) => !!error && /expired/i.test(error)

type TokenErrorMessageProps = {
  icon: ReactNode
  title: string
  description: string
}

function TokenErrorMessage({ icon, title, description }: TokenErrorMessageProps) {
  return (
    <Box display="flex" flexDirection="column" alignItems="center" textAlign="center" py={3}>
      {icon}
      <Typography variant="subtitle1" fontWeight={600}>
        {title}
      </Typography>
      <Typography variant="body2" sx={{ mt: 1, color: 'text.tertiary', maxWidth: 320 }}>
        {description}
      </Typography>
    </Box>
  )
}

type CompanyBannerCompactProps = {
  envId: string | undefined
  apiKey: string | undefined
  isLoading: boolean
  error: string | undefined
  hasError?: boolean
}

export function CompanyBannerCompact({
  envId,
  apiKey,
  isLoading: parentLoading,
  error,
  hasError,
}: CompanyBannerCompactProps) {
  const hasTokenError = hasError ?? (!parentLoading && !envId)
  const { data, isLoading } = usePreferences(hasTokenError ? '' : (envId ?? ''), hasTokenError ? '' : (apiKey ?? ''))

  useEffect(() => {
    if (data?.tabTitle) document.title = data.tabTitle

    if (data?.base64Favicon) {
      const favicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement | null
      if (favicon) favicon.href = data.base64Favicon
    }
  }, [data])

  const loading = parentLoading || (!hasTokenError && isLoading)

  if (hasTokenError) {
    if (isExpiredLink(error)) {
      return (
        <TokenErrorMessage
          icon={<LinkOff sx={{ fontSize: 40, mb: 2, color: 'text.primary' }} />}
          title="This link has expired"
          description="Please request a new link from your administrator."
        />
      )
    }

    return (
      <TokenErrorMessage
        icon={<ErrorOutline sx={{ fontSize: 40, mb: 2, color: 'text.primary' }} />}
        title="Something went wrong"
        description={error ? `${error}, please contact the admin.` : 'Something went wrong, please try again.'}
      />
    )
  }

  return (
    <Box display="flex" alignItems="center" gap={2} pb={3} mb={1} sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
      {loading ? (
        <Skeleton width={40} height={40} variant="circular" sx={{ flexShrink: 0 }} />
      ) : data?.connectLogoData ? (
        <Box
          component="img"
          src={data.connectLogoData}
          alt=""
          sx={{ width: 40, height: 40, objectFit: 'contain', flexShrink: 0 }}
        />
      ) : (
        <Box sx={{ flexShrink: 0 }}>
          <ReactSVG
            src={icon}
            beforeInjection={(svg) => {
              svg.setAttribute('width', '40')
              svg.setAttribute('height', '40')
            }}
          />
        </Box>
      )}

      <Box minWidth={0}>
        <Typography variant="subtitle1" fontWeight={600} noWrap>
          {loading ? (
            <Skeleton width={140} variant="text" />
          ) : (
            data?.connectTitle ?? 'Connect to hotglue'
          )}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: error ? 'error.main' : 'text.tertiary' }}
        >
          {loading ? (
            <Skeleton width={200} variant="text" />
          ) : error ? (
            data?.connectError || `${error}, please contact the admin.`
          ) : (
            data?.connectDescription || 'Start connecting your apps to Hotglue. Select a provider to get started.'
          )}
        </Typography>
      </Box>
    </Box>
  )
}
